import React, { useEffect, useState } from "react";
import { readExcel } from "../../utils/excelfunction";
import { usePostApi } from "../../hooks/post_api_hook/usePostApi";
import { useClasseContext } from "../../context/ClasseContext";
import { useNavigate } from "react-router-dom";
import { url } from "../../utils/confserveur";
import TemplateImportEcoleRattachement from "./TemplateImportEcoleRattachement";
import { normalizeString } from "../../utils/excelfunction";
import { useRef } from "react";
import { School, Upload, FileSpreadsheet, AlertTriangle, CheckCircle, Users, ArrowRight, RefreshCw, X } from "lucide-react";

function ImportEcoleRattachement({ eleves, onClose }) {

  const navigate = useNavigate();
  const { classeNom } = useClasseContext();
  const [dataElevesWithEcole, setDataElevesWithEcole] = useState(null);
  const [errorLoad, setErrorLoad] = useState([]);
  const [dragOver, setDragOver] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const fileInputRef = useRef(null);

  const [{ data: datapost, isLoading: isLoadingPost, isError: isErrorPost }, doPost] = usePostApi(url + "eleves/update-ecoles/");

  const normalizeKey = (str) => {
    if (!str) return str;
    if (typeof str !== 'string') str = String(str);
    return str
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      .trim();
  };

  const verifMatchNoms = (eleves, excelData) => {
    const erreurs = [];

    excelData.forEach((row, index) => {
      const nomExcel = normalizeString(row.NOM?.toUpperCase().trim());
      const prenomExcel = normalizeString(row.PRENOM?.toUpperCase().trim());
      const classeExcel = normalizeString(row.CLASSE?.toUpperCase().trim());
      const eleveTrouve = eleves.some(
        (eleve) =>
          normalizeString(eleve.nom.toUpperCase()) === nomExcel &&
        normalizeString(eleve.prenom.toUpperCase()) === prenomExcel &&
        normalizeString(eleve.classe_nom) === classeExcel
      );

      if (!eleveTrouve) {
        erreurs.push({
          ligne: index + 1,
          message: `L'élève ${prenomExcel} ${nomExcel} de la ${classeExcel} n'existe pas dans TanoEd.`,
        });
      }
    });

    return erreurs;
  };

  const verifMatchClasse = (excelData, listeClasses) => {
    const erreurs = [];

    const classesValides = listeClasses.map((classe) => classe.toUpperCase().trim());

    excelData.forEach((row, index) => {
      const classeExcel = row.CLASSE?.toUpperCase().trim();

      if (!classesValides.includes(classeExcel)) {
        erreurs.push(
          `Erreur à la ligne ${index + 1} : La classe "${classeExcel}" ne correspond à aucune classe valide (${classesValides.join(', ')}).`
        );
      }
    });

    return erreurs;
  };

  // Vérifier que toutes les classes sont des 6èmes
  const verifClasses6eme = (excelData) => {
    const erreurs = [];

    excelData.forEach((row, index) => {
      const classeExcel = row.CLASSE?.trim();

      if (classeExcel && !classeExcel.startsWith('6')) {
        erreurs.push({
          ligne: index + 1,
          message: `La classe "${classeExcel}" n'est pas une classe de 6ème. Seules les classes de 6ème sont acceptées.`,
        });
      }
    });

    return erreurs;
  };

  const verifFormatEcole = (excelData) => {
    const erreurs = [];

    excelData.forEach((row, index) => {
      const ecole = row.ECOLE?.trim();

      if (!ecole || !ecole.includes(" - ")) {
        erreurs.push({
          ligne: index + 1,
          message: `L'école "${ecole}" n'est pas au format attendu (CODE - NOM DU CLG - ADRESSE).`,
        });
        return;
      }

      const [code, nomEcole, ...adresse] = ecole.split(" - ");

      if (!code || !nomEcole) {
        erreurs.push({
          ligne: index + 1,
          message: `L'école "${ecole}" doit contenir un code et un nom d'établissement.`,
        });
      }

      if (adresse.length === 0) {
        erreurs.push({
          ligne: index + 1,
          message: `L'école "${ecole}" semble manquer d'une adresse ou de son code établissement.`,
        });
      }
    });

    return erreurs;
  }

  const standardizeHeaders = (data) => {
    return data.map(item => {
      const standardizedItem = {
        NOM: normalizeKey(item.NOM || item.Nom || item['Nom de famille'] || item['Last Name']),
        PRENOM: normalizeKey(item.PRENOM || item.Prenom || item['Prénom'] || item['First Name'] || item['PRÉNOM']),
        CLASSE: normalizeKey(item.CLASSE || item.Classe || item['Classe']),
        ECOLE: normalizeKey(item.ECOLE || item.Ecole || item['École'] || item['School'] || item["Étab. d'origine"]
           || item["Etab. d'origine"] || item.Etab || item.ETAB || item['Étab'])
      };

      return standardizedItem;
    });
  };

  const handleFiles = async (file) => {
    setErrorLoad([])
    setSelectedFile(file)
    if (file && file instanceof Blob) {
      try {
        const sheetsData = await readExcel(file);
        const standardizedData = standardizeHeaders(sheetsData[0]);
        // Vérifier que les en-têtes ont bien été trouvés (pas undefined)
        const firstRow = standardizedData[0];
        const missingHeaders = ['CLASSE', 'NOM', 'PRENOM', 'ECOLE'].filter(h => firstRow[h] === undefined);
        if(missingHeaders.length === 0){
          const erreurs = verifierDonneesExcel(eleves, standardizedData, classeNom);
          if (erreurs.length === 0) {
            if(verifierElevesAvecEcoleDiff(eleves,standardizedData)){
              setDataElevesWithEcole(verifierElevesAvecEcoleDiff(eleves,standardizedData));
            }
          } else {
            setErrorLoad(erreurs);
          }
        }
        else{
          setErrorLoad(['Les en-têtes de votre fichier ne sont pas corrects. Téléchargez le modèle Excel pour voir un aperçu des en-têtes attendus.'])
        }
      } catch (error) {
        console.error(error);
        setErrorLoad(["Une erreur s'est produite lors de la lecture du fichier."]);
      }
    } else {
      setErrorLoad(["Aucun fichier valide fourni."]);
    }
  };

  const verifierDonneesExcel = (eleves, excelData, classeNom) => {
    // Vérifier d'abord que toutes les classes sont des 6èmes
    const erreurs6eme = verifClasses6eme(excelData);
    if (erreurs6eme.length > 0) {
      return erreurs6eme;
    }

    const classesUniques = Array.from(
      new Set(eleves.map((eleve) => eleve.classe_nom))
    );
    const erreursClasse = verifMatchClasse(excelData, classesUniques);
    const erreursNoms = verifMatchNoms(eleves,excelData)
    const erreursFormatEcole = verifFormatEcole(excelData)

    const toutesLesErreurs = [
      ...erreursClasse,
      ...erreursNoms,
      ...erreursFormatEcole,
    ];

    if (toutesLesErreurs.length > 0) {
      console.error("Des erreurs ont été détectées :", toutesLesErreurs);
      return toutesLesErreurs;
    }

    return [];
  };

  const verifierElevesAvecEcoleDiff = (eleves, excelData) => {
    const elevesAMettreAJour = [];

    excelData.forEach((row) => {
      const [code, nom, ...adresseParts] = row.ECOLE.split(" - ");
      const adresse = adresseParts.join(" - ").trim();
      const ecoleExcel = {
        code: code.trim(),
        nom: nom.trim(),
        adresse: adresse.trim(),
      };

      const eleveTrouve = eleves.find(
        (eleve) =>
          normalizeString(eleve.nom.toUpperCase()) === normalizeString(row.NOM.toUpperCase().trim()) &&
        normalizeString(eleve.prenom.toUpperCase()) === normalizeString(row.PRENOM.toUpperCase().trim())&&
        normalizeString(eleve.classe_nom.toUpperCase()) === normalizeString(row.CLASSE.toUpperCase().trim())
      );

      if (eleveTrouve) {
        if (
          !eleveTrouve.ecole_nom ||
          eleveTrouve.ecole_code !== ecoleExcel.code
        ) {
          elevesAMettreAJour.push({
            id: eleveTrouve.id,
            nom: eleveTrouve.nom,
            prenom: eleveTrouve.prenom,
            classe_nom: eleveTrouve.classe_nom,
            ancienneEcole: eleveTrouve.ecole_nom,
            nouvelleEcole: ecoleExcel,
          });
        }
      }
    });

    if(elevesAMettreAJour.length===0){
      setErrorLoad(["Toutes les écoles renseignés pour les élèves présents sur votre fichier CSV sont identique à ceux de TanoEd."])
    }
    return elevesAMettreAJour;
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    setDragOver(true);
  };

  const handleDragLeave = () => {
    setDragOver(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    const files = e.dataTransfer.files;
    if (files.length > 0) {
      setSelectedFile(files[0]);
      handleFiles(files[0]);
    }
  };

  const handleFileInputChange = (e) => {
    if (e.target.files.length > 0) {
      handleFiles(e.target.files[0]);
    }
    // Réinitialise l'input pour permettre de re-sélectionner le même fichier
    e.target.value = '';
  };

  const openFileDialog = () => {
    fileInputRef.current?.click();
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    const jsonData = JSON.stringify(dataElevesWithEcole, null, 2);
    await doPost(jsonData);

    if (isErrorPost) {
      console.error("Erreur lors de l'import des données : ", isErrorPost);
    }
  };

  const handleBackToImport = () => {
    if (onClose) {
      onClose();
    }
    navigate(0);
  };

  // Couleurs du thème
  const colors = {
    gradient: 'from-blue-600 via-cyan-500 to-sky-400',
    blob1: '#67e8f9',
    blob2: '#a5f3fc',
  };

  return (
    <div className="w-full flex justify-center">
      <div className="w-fit bg-white rounded-3xl shadow-2xl overflow-hidden">

        {/* Section d'importation */}
        {!datapost && eleves && eleves.length > 0 && (
          <>
            {/* Header avec gradient et blobs animés */}
            <div className="relative overflow-hidden bg-gradient-to-r from-blue-600 via-cyan-500 to-sky-400 px-8 xl:px-5 2xl:px-6 3xl:px-8 py-10 xl:py-6 2xl:py-8 3xl:py-10">
              {/* Bouton fermer */}
              {onClose && (
                <button
                  onClick={onClose}
                  className="absolute top-4 right-4 z-20 p-2 bg-orange-500 backdrop-blur-sm rounded-xl text-white hover:bg-orange-600 transition-colors"
                >
                  <X size={20} />
                </button>
              )}

              {/* Blobs animés */}
              <svg className="absolute top-0 left-0 w-full h-full opacity-30" viewBox="0 0 200 200">
                <circle cx="20" cy="20" r="40" fill={colors.blob1}>
                  <animate attributeName="cx" values="20;60;20" dur="8s" repeatCount="indefinite" />
                  <animate attributeName="cy" values="20;50;20" dur="6s" repeatCount="indefinite" />
                </circle>
                <circle cx="180" cy="80" r="30" fill={colors.blob2}>
                  <animate attributeName="cx" values="180;140;180" dur="7s" repeatCount="indefinite" />
                  <animate attributeName="cy" values="80;120;80" dur="9s" repeatCount="indefinite" />
                </circle>
                <circle cx="100" cy="150" r="35" fill="#22d3ee">
                  <animate attributeName="cx" values="100;130;100" dur="10s" repeatCount="indefinite" />
                  <animate attributeName="cy" values="150;170;150" dur="5s" repeatCount="indefinite" />
                </circle>
              </svg>

              <div className="relative z-10 text-center">
                <div className="flex justify-center mb-4 xl:mb-3 2xl:mb-3.5 3xl:mb-4">
                  <div className="p-4 xl:p-2.5 2xl:p-3 3xl:p-4 bg-white/20 backdrop-blur-sm rounded-2xl">
                    <School className="text-white xl:w-7 xl:h-7 2xl:w-8 2xl:h-8 3xl:w-10 3xl:h-10" size={40} />
                  </div>
                </div>
                <h1 className="text-3xl xl:text-xl 2xl:text-2xl 3xl:text-3xl font-bold text-white mb-2 xl:mb-1.5 2xl:mb-1.5 3xl:mb-2">
                  Écoles de rattachement
                </h1>
                <p className="text-cyan-100 text-lg xl:text-sm 2xl:text-base 3xl:text-lg">
                  Importez les écoles d'origine de vos élèves de 6ème
                </p>
              </div>
            </div>

            {/* Contenu */}
            <div className="p-8 xl:p-5 2xl:p-6 3xl:p-8">
              {/* Statistiques élèves */}
              <div className="mb-6 xl:mb-4 2xl:mb-5 3xl:mb-6 p-4 xl:p-3 2xl:p-3.5 3xl:p-4 bg-gradient-to-r from-blue-50 to-cyan-50 border border-blue-200/50 rounded-2xl">
                <div className="flex items-center gap-3 xl:gap-2 2xl:gap-2.5 3xl:gap-3">
                  <div className="p-2 xl:p-1.5 2xl:p-1.5 3xl:p-2 bg-blue-100 rounded-xl">
                    <Users size={20} className="text-blue-600 xl:w-4 xl:h-4 2xl:w-[18px] 2xl:h-[18px] 3xl:w-5 3xl:h-5" />
                  </div>
                  <div>
                    <p className="font-semibold text-blue-800 xl:text-sm 2xl:text-sm 3xl:text-base">{eleves.length} élèves disponibles</p>
                    <p className="text-sm xl:text-xs 2xl:text-xs 3xl:text-sm text-blue-600">Prêts pour l'import des écoles de rattachement</p>
                  </div>
                </div>
              </div>

              {/* Zone de drag & drop */}
              <div
                className={`relative group border-2 border-dashed rounded-2xl p-8 xl:p-5 2xl:p-6 3xl:p-8 mb-6 xl:mb-4 2xl:mb-5 3xl:mb-6 transition-all duration-300 cursor-pointer ${
                  dragOver
                    ? 'border-cyan-500 bg-cyan-50 scale-[1.02]'
                    : selectedFile && errorLoad.length === 0
                      ? 'border-emerald-500 bg-emerald-50'
                      : 'border-slate-300 bg-slate-50 hover:border-cyan-400 hover:bg-cyan-50/50'
                }`}
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
                onClick={openFileDialog}
              >
                {/* Glow effect */}
                <div className={`absolute -inset-0.5 bg-gradient-to-r from-cyan-500 to-blue-500 rounded-2xl blur opacity-0
                  ${dragOver ? 'opacity-30' : 'group-hover:opacity-20'} transition duration-300`} />

                <input
                  type="file"
                  ref={fileInputRef}
                  onChange={handleFileInputChange}
                  accept=".csv,.xlsx,.xls"
                  className="hidden"
                />

                <div className="relative text-center">
                  <div className="flex justify-center mb-4 xl:mb-3 2xl:mb-3.5 3xl:mb-4">
                    <div className={`p-4 xl:p-2.5 2xl:p-3 3xl:p-4 rounded-2xl transition-all duration-300 ${
                      selectedFile && errorLoad.length === 0
                        ? 'bg-emerald-100'
                        : 'bg-gradient-to-br from-blue-500 to-cyan-500'
                    }`}>
                      {selectedFile && errorLoad.length === 0 ? (
                        <CheckCircle size={32} className="text-emerald-600 xl:w-6 xl:h-6 2xl:w-7 2xl:h-7 3xl:w-8 3xl:h-8" />
                      ) : (
                        <Upload size={32} className="text-white xl:w-6 xl:h-6 2xl:w-7 2xl:h-7 3xl:w-8 3xl:h-8" />
                      )}
                    </div>
                  </div>

                  <p className="text-lg xl:text-sm 2xl:text-base 3xl:text-lg font-semibold text-slate-700 mb-2 xl:mb-1.5 2xl:mb-1.5 3xl:mb-2">
                    {selectedFile && errorLoad.length === 0
                      ? `Fichier sélectionné: ${selectedFile.name}`
                      : 'Glissez votre fichier ici'
                    }
                  </p>
                  <p className="text-sm xl:text-xs 2xl:text-xs 3xl:text-sm text-slate-500">
                    {selectedFile && errorLoad.length === 0
                      ? 'Fichier prêt pour l\'import !'
                      : 'ou cliquez pour parcourir (CSV, XLSX, XLS)'
                    }
                  </p>
                </div>
              </div>

             

              {/* Template téléchargement */}
              <div className="mb-6 xl:mb-4 2xl:mb-5 3xl:mb-6">
                <TemplateImportEcoleRattachement />
              </div>

              {/* Erreurs */}
              {errorLoad.length > 0 && (
                <div className="mb-6 xl:mb-4 2xl:mb-5 3xl:mb-6 p-4 xl:p-3 2xl:p-3.5 3xl:p-4 bg-gradient-to-r from-amber-50 to-orange-50 border border-amber-200/50 rounded-2xl">
                  <div className="flex items-start gap-3 xl:gap-2 2xl:gap-2.5 3xl:gap-3">
                    <div className="p-2 xl:p-1.5 2xl:p-1.5 3xl:p-2 bg-amber-100 rounded-xl shrink-0">
                      <AlertTriangle size={20} className="text-amber-600 xl:w-4 xl:h-4 2xl:w-[18px] 2xl:h-[18px] 3xl:w-5 3xl:h-5" />
                    </div>
                    <div>
                      <p className="font-semibold text-amber-800 mb-2 xl:mb-1.5 2xl:mb-1.5 3xl:mb-2 xl:text-sm 2xl:text-sm 3xl:text-base">Erreurs détectées</p>
                      <ul className="space-y-1">
                        {errorLoad.map((e, idx) => (
                          <li key={idx} className="text-sm xl:text-xs 2xl:text-xs 3xl:text-sm text-amber-700 flex items-start gap-2 xl:gap-1.5 2xl:gap-1.5 3xl:gap-2">
                            <ArrowRight size={14} className="text-amber-500 mt-1 shrink-0 xl:w-3 xl:h-3 2xl:w-3.5 2xl:h-3.5 3xl:w-3.5 3xl:h-3.5" />
                            {typeof e === "string" ? e : e.message}
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </div>
              )}

              {/* Tableau des élèves à mettre à jour */}
              {dataElevesWithEcole && dataElevesWithEcole.length > 0 && (
                <div className="mb-6 xl:mb-4 2xl:mb-5 3xl:mb-6">
                  <h3 className="text-lg xl:text-sm 2xl:text-base 3xl:text-lg font-semibold text-slate-800 mb-4 xl:mb-3 2xl:mb-3.5 3xl:mb-4 flex items-center gap-2 xl:gap-1.5 2xl:gap-1.5 3xl:gap-2">
                    <span className="w-2 h-2 rounded-full bg-gradient-to-r from-blue-500 to-cyan-500"></span>
                    {dataElevesWithEcole.length} élève(s) à mettre à jour
                  </h3>

                  <div className="overflow-hidden rounded-2xl border border-slate-200 shadow-lg">
                    <div className="overflow-x-auto max-h-[400px]">
                      <table className="min-w-full">
                        <thead className="sticky top-0 bg-gradient-to-r from-blue-600 to-cyan-500 text-white">
                          <tr>
                            <th className="px-4 xl:px-3 2xl:px-3.5 3xl:px-4 py-3 xl:py-2 2xl:py-2.5 3xl:py-3 text-left text-xs xl:text-[10px] 2xl:text-[11px] 3xl:text-xs font-semibold uppercase tracking-wider">Type</th>
                            <th className="px-4 xl:px-3 2xl:px-3.5 3xl:px-4 py-3 xl:py-2 2xl:py-2.5 3xl:py-3 text-left text-xs xl:text-[10px] 2xl:text-[11px] 3xl:text-xs font-semibold uppercase tracking-wider">Classe</th>
                            <th className="px-4 xl:px-3 2xl:px-3.5 3xl:px-4 py-3 xl:py-2 2xl:py-2.5 3xl:py-3 text-left text-xs xl:text-[10px] 2xl:text-[11px] 3xl:text-xs font-semibold uppercase tracking-wider">Nom</th>
                            <th className="px-4 xl:px-3 2xl:px-3.5 3xl:px-4 py-3 xl:py-2 2xl:py-2.5 3xl:py-3 text-left text-xs xl:text-[10px] 2xl:text-[11px] 3xl:text-xs font-semibold uppercase tracking-wider">Prénom</th>
                            <th className="px-4 xl:px-3 2xl:px-3.5 3xl:px-4 py-3 xl:py-2 2xl:py-2.5 3xl:py-3 text-left text-xs xl:text-[10px] 2xl:text-[11px] 3xl:text-xs font-semibold uppercase tracking-wider">Nouvelle école</th>
                          </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-slate-100">
                          {dataElevesWithEcole.map((item, index) => (
                            <tr key={index} className="hover:bg-cyan-50/50 transition-colors">
                              <td className="px-4 xl:px-3 2xl:px-3.5 3xl:px-4 py-3 xl:py-2 2xl:py-2.5 3xl:py-3">
                                {item.ancienneEcole ? (
                                  <span className="px-2 xl:px-1.5 2xl:px-1.5 3xl:px-2 py-1 xl:py-0.5 2xl:py-0.5 3xl:py-1 bg-amber-100 text-amber-700 text-xs xl:text-[10px] 2xl:text-[11px] 3xl:text-xs font-medium rounded-lg">
                                    Modification
                                  </span>
                                ) : (
                                  <span className="px-2 xl:px-1.5 2xl:px-1.5 3xl:px-2 py-1 xl:py-0.5 2xl:py-0.5 3xl:py-1 bg-emerald-100 text-emerald-700 text-xs xl:text-[10px] 2xl:text-[11px] 3xl:text-xs font-medium rounded-lg">
                                    Ajout
                                  </span>
                                )}
                              </td>
                              <td className="px-4 xl:px-3 2xl:px-3.5 3xl:px-4 py-3 xl:py-2 2xl:py-2.5 3xl:py-3 text-sm xl:text-xs 2xl:text-xs 3xl:text-sm font-medium text-slate-800">{item.classe_nom}</td>
                              <td className="px-4 xl:px-3 2xl:px-3.5 3xl:px-4 py-3 xl:py-2 2xl:py-2.5 3xl:py-3 text-sm xl:text-xs 2xl:text-xs 3xl:text-sm text-slate-600">{item.nom}</td>
                              <td className="px-4 xl:px-3 2xl:px-3.5 3xl:px-4 py-3 xl:py-2 2xl:py-2.5 3xl:py-3 text-sm xl:text-xs 2xl:text-xs 3xl:text-sm text-slate-600">{item.prenom}</td>
                              <td className="px-4 xl:px-3 2xl:px-3.5 3xl:px-4 py-3 xl:py-2 2xl:py-2.5 3xl:py-3">
                                <div className="text-sm xl:text-xs 2xl:text-xs 3xl:text-sm">
                                  {item.ancienneEcole && (
                                    <div className="text-slate-400 line-through mb-1">
                                      {item.ancienneEcole}
                                    </div>
                                  )}
                                  <div className="text-slate-700 font-medium">
                                    {item.nouvelleEcole.code} - {item.nouvelleEcole.nom}
                                  </div>
                                  <div className="text-slate-500 text-xs">
                                    {item.nouvelleEcole.adresse}
                                  </div>
                                </div>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>

                  {/* Bouton soumettre */}
                  {errorLoad.length === 0 && (
                    <form className="mt-6 xl:mt-4 2xl:mt-5 3xl:mt-6" onSubmit={handleSubmit}>
                      <button
                        type="submit"
                        disabled={isLoadingPost}
                        className="w-full py-4 xl:py-3 2xl:py-3.5 3xl:py-4 px-6 xl:px-4 2xl:px-5 3xl:px-6 bg-gradient-to-r from-emerald-500 to-green-600 text-white text-lg xl:text-sm 2xl:text-base 3xl:text-lg font-semibold
                          rounded-xl shadow-lg shadow-emerald-500/30 hover:shadow-xl hover:shadow-emerald-500/40
                          transition-all duration-300 hover:scale-[1.02] active:scale-[0.98]
                          disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100
                          flex items-center justify-center gap-3 xl:gap-2 2xl:gap-2.5 3xl:gap-3"
                      >
                        {isLoadingPost ? (
                          <>
                            <RefreshCw size={22} className="animate-spin xl:w-4 xl:h-4 2xl:w-5 2xl:h-5 3xl:w-[22px] 3xl:h-[22px]" />
                            Mise à jour en cours...
                          </>
                        ) : (
                          <>
                            <CheckCircle size={22} className="xl:w-4 xl:h-4 2xl:w-5 2xl:h-5 3xl:w-[22px] 3xl:h-[22px]" />
                            Mettre à jour les écoles de rattachement
                          </>
                        )}
                      </button>
                    </form>
                  )}
                </div>
              )}
            </div>
          </>
        )}

        {/* Section résultat d'importation */}
        {datapost && (
          <div className="p-8 xl:p-5 2xl:p-6 3xl:p-8">
            {datapost.success ? (
              <div className="text-center">
                <div className="flex justify-center mb-6 xl:mb-4 2xl:mb-5 3xl:mb-6">
                  <div className="p-4 xl:p-2.5 2xl:p-3 3xl:p-4 bg-emerald-100 rounded-2xl">
                    <CheckCircle size={48} className="text-emerald-600 xl:w-8 xl:h-8 2xl:w-10 2xl:h-10 3xl:w-12 3xl:h-12" />
                  </div>
                </div>
                <h2 className="text-2xl xl:text-lg 2xl:text-xl 3xl:text-2xl font-bold text-slate-800 mb-2 xl:mb-1.5 2xl:mb-1.5 3xl:mb-2">Import réussi !</h2>
                <p className="text-slate-600 mb-6 xl:mb-4 2xl:mb-5 3xl:mb-6 xl:text-sm 2xl:text-sm 3xl:text-base">Les écoles de rattachement ont été mises à jour avec succès.</p>

                <div className="p-4 xl:p-3 2xl:p-3.5 3xl:p-4 bg-gradient-to-r from-emerald-50 to-green-50 border border-emerald-200/50 rounded-2xl mb-6 xl:mb-4 2xl:mb-5 3xl:mb-6">
                  <p className="text-emerald-700 font-medium xl:text-sm 2xl:text-sm 3xl:text-base">
                    Toutes les données ont été enregistrées correctement.
                  </p>
                </div>
              </div>
            ) : (
              <div className="text-center">
                <div className="flex justify-center mb-6 xl:mb-4 2xl:mb-5 3xl:mb-6">
                  <div className="p-4 xl:p-2.5 2xl:p-3 3xl:p-4 bg-red-100 rounded-2xl">
                    <AlertTriangle size={48} className="text-red-600 xl:w-8 xl:h-8 2xl:w-10 2xl:h-10 3xl:w-12 3xl:h-12" />
                  </div>
                </div>
                <h2 className="text-2xl xl:text-lg 2xl:text-xl 3xl:text-2xl font-bold text-slate-800 mb-2 xl:mb-1.5 2xl:mb-1.5 3xl:mb-2">Échec de l'importation</h2>

                <div className="p-4 xl:p-3 2xl:p-3.5 3xl:p-4 bg-gradient-to-r from-red-50 to-rose-50 border border-red-200/50 rounded-2xl mb-6 xl:mb-4 2xl:mb-5 3xl:mb-6 text-left">
                  <p className="text-red-700 font-medium mb-2 xl:mb-1.5 2xl:mb-1.5 3xl:mb-2 xl:text-sm 2xl:text-sm 3xl:text-base">{datapost.message}</p>
                  <p className="text-red-600 text-sm xl:text-xs 2xl:text-xs 3xl:text-sm">
                    Veuillez vérifier les noms, prénoms et classes dans votre fichier, puis réessayez.
                  </p>
                </div>
              </div>
            )}

            <button
              onClick={handleBackToImport}
              className="w-full py-4 xl:py-3 2xl:py-3.5 3xl:py-4 px-6 xl:px-4 2xl:px-5 3xl:px-6 bg-gradient-to-r from-blue-600 to-cyan-500 text-white text-lg xl:text-sm 2xl:text-base 3xl:text-lg font-semibold
                rounded-xl shadow-lg shadow-cyan-500/30 hover:shadow-xl hover:shadow-cyan-500/40
                transition-all duration-300 hover:scale-[1.02] active:scale-[0.98]
                flex items-center justify-center gap-3 xl:gap-2 2xl:gap-2.5 3xl:gap-3"
            >
              <RefreshCw size={22} className="xl:w-4 xl:h-4 2xl:w-5 2xl:h-5 3xl:w-[22px] 3xl:h-[22px]" />
              Continuer
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

export default ImportEcoleRattachement;
